import { useEffect, useMemo, useState } from 'react';
import { useReactiveVar } from '@apollo/client';
import LinearProgress from '@material-ui/core/LinearProgress';
import cartAPI from '../lib/cartAPI';
import cartItemsVar from '../lib/cartItemsVar';
import Modal from './Modal';

export default function AddToCartButton({ children, domainsCSV }) {
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const cartItems = useReactiveVar(cartItemsVar);
  const domains = useMemo(() => domainsCSV.split(','), [domainsCSV]);
  const isBulk = domains.length > 1;
  const inCart = domains.every(domain => cartItems.includes(domain));

  useEffect(() => {
    let miniCartEvent;
    const finishedProcessing = progress === domains.length;
    if (!busy) {
      if (progress !== 0 && !finishedProcessing) {
        miniCartEvent = new CustomEvent('domainSearch.cancelAddAllToCart', {
          detail: progress,
        });
      }
    } else if (finishedProcessing) {
      if (isBulk) {
        miniCartEvent = new CustomEvent('domainSearch.addAllToCart', {
          detail: domains.length,
        });
      } else {
        miniCartEvent = new CustomEvent('domainSearch.addToCart', {
          detail: domains[0],
        });
      }
      // Reset state
      setProgress(0);
      setBusy(false);
    } else {
      const cartItems = cartItemsVar();
      const domain = domains[progress];
      if (cartItems.includes(domain)) {
        setProgress(i => i + 1);
      } else {
        cartAPI
          .post('/user/additems', {
            Items: [
              {
                Key: `${Date.now()}:${domain}`,
                Product: 'ncmarketplace',
                Action: 'PURCHASE',
                Duration: {
                  Value: 1,
                  DurationType: 'YEAR',
                },
                Qty: 1,
                Enabled: true,
              },
            ],
          })
          .then(({ data }) => {
            if (data[0]?.MessageKey === 'ERROR') {
              throw new Error(data[0].LocalizedMessage);
            } else {
              setProgress(i => i + 1);
              cartItemsVar([domain, ...cartItemsVar()]);
            }
          })
          .catch(error => {
            console.error(error);
            window.alert(
              `Sorry, we were unable to add domain ${domains[progress]} to shopping cart. Please reach out to us directly to purchase this domain.`,
            );
            setBusy(false);
          });
      }
    }
    if (miniCartEvent) {
      document.dispatchEvent(miniCartEvent);
    }
  }, [busy, domains, isBulk, progress]);

  return (
    <>
      {children({ busy, inCart, onClick: () => setBusy(true) })}
      {isBulk ? (
        <Modal
          onClose={() => {
            setBusy(false);
          }}
          open={busy}>
          <h2
            className="tw-text-2xl tw-font-bold tw-text-gray-700 tw-mb-2"
            id="modal-title">
            Adding Domains
          </h2>
          <p id="modal-description">
            The domains you selected are being added to your shopping cart. This
            may take a couple minutes.
          </p>
          <div className="tw-my-6 tw-border tw-border-gray-400 tw-rounded tw-overflow-hidden">
            <div className="tw-flex tw-items-center tw-p-4">
              <div className="tw-flex-auto">
                Processing:
                <span className="tw-ml-2 tw-text-gray-700 tw-font-medium">
                  {progress} of {domains.length} domains
                </span>
              </div>
              <div className="gb-loader gb-loader--secondary" />
            </div>
            <LinearProgress
              color="secondary"
              value={Math.floor((progress / domains.length) * 100)}
              variant="determinate"
            />
          </div>
          <div className="tw-flex tw-justify-end tw-mt-4">
            <button
              className="gb-btn gb-btn--lg"
              onClick={() => {
                setBusy(false);
              }}>
              Cancel
            </button>
          </div>
        </Modal>
      ) : null}
    </>
  );
}
